import { userHasPermission } from '@/shared/lib/roleUtils'

/**
 * Quién agenda la entrevista del solicitante y a quién se pone de
 * entrevistador (SRH-308).
 *
 * Esa entrevista la agenda el solicitante del departamento, y también RH y
 * administración en su nombre. Antes solo la ofrecían las pantallas que
 * exigían ser quien crea solicitudes, así que una cuenta de RH no tenía cómo
 * agendarla desde la ficha ni desde el tablero: solo le quedaba la pantalla de
 * Entrevistas. Si el solicitante no la agendaba, el candidato se quedaba
 * esperando sin que nadie más pudiera moverlo.
 */

const PERMISO_DE_AGENDAR = 'rh.entrevistas.base.create'
const PERMISO_DE_SOLICITANTE = 'rh.solicitudes_personal.base.create'

/**
 * Si puede agendar la entrevista del solicitante.
 *
 * Es la misma regla del servidor para crearla (`POST /hiring-manager-interviews`
 * acepta cualquiera de las dos llaves). Mantenerlas iguales evita ofrecer un
 * botón que termina en 403.
 */
export const puedeAgendarEntrevistaDelSolicitante = (
  permisos: string[] = [],
): boolean =>
  userHasPermission(PERMISO_DE_AGENDAR, permisos) ||
  userHasPermission(PERMISO_DE_SOLICITANTE, permisos)

/**
 * Si puede tomar la decisión del solicitante (aprobar o rechazar).
 *
 * Es aparte de agendar a propósito: agendar «en su nombre» no es decidir en su
 * nombre. La aprobación del solicitante la da él, dueño de la vacante, y RH
 * cierra después.
 */
export const puedeDecidirComoSolicitante = (permisos: string[] = []): boolean =>
  userHasPermission(PERMISO_DE_SOLICITANTE, permisos)

/**
 * A quién se propone de entrevistador.
 *
 * Quien agenda en nombre del solicitante no es quien va a entrevistar: sin
 * esto el formulario ponía por omisión a quien estuviera logueado, y la
 * entrevista quedaba a nombre de RH. Si el solicitante no está entre los
 * entrevistadores disponibles se queda quien agenda, para no dejar el campo
 * vacío ni inventar a alguien que la lista no ofrece.
 */
export function entrevistadorSugerido({
  solicitanteId,
  usuarioId,
  entrevistadoresDisponibles,
}: {
  solicitanteId?: string | null
  usuarioId?: string | null
  entrevistadoresDisponibles: Array<{ id: string }>
}): string {
  const solicitanteEsElegible =
    !!solicitanteId &&
    entrevistadoresDisponibles.some((e) => e.id === solicitanteId)

  if (solicitanteEsElegible) return solicitanteId as string
  return usuarioId ?? ''
}

/** Lo que dice la tarjeta de la ficha, según quién la mira y qué pide la vacante. */
export function textoDeLaEntrevistaDelSolicitante({
  puedeDecidir,
  pideEntrevistaFinal,
}: {
  puedeDecidir: boolean
  pideEntrevistaFinal: boolean
}): string {
  if (!puedeDecidir) {
    return 'Agenda la entrevista con el solicitante en su nombre. La decisión la toma él al cargar el resultado.'
  }
  return pideEntrevistaFinal
    ? 'Esta vacante pide entrevista del solicitante: agéndala y la decisión se registra al cargar su resultado.'
    : 'Agenda una entrevista con el solicitante, o decide sobre este candidato.'
}
